import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { Wordmark } from "../components/Wordmark";
import { toast } from "sonner";
import { Gift as GiftIcon, Loader2, Copy, Check, Download } from "lucide-react";

export default function GiftSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ loading: true, paid: false, code: null, recipient: "" });
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const downloadCard = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/gifts/${sessionId}/card`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `write-my-story-gift-${state.code}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Couldn't prepare the gift card. Please try again."); }
    finally { setDownloading(false); }
  };

  useEffect(() => {
    if (!sessionId) { setState((s) => ({ ...s, loading: false })); return; }
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/gifts/status/${sessionId}`);
        if (data.payment_status === "paid" && data.gift_code) {
          setState({ loading: false, paid: true, code: data.gift_code, recipient: data.recipient_name || "" });
          return;
        }
      } catch { /* keep trying */ }
      tries += 1;
      if (tries < 20) timer = setTimeout(poll, 2000);
      else setState((s) => ({ ...s, loading: false }));
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  const copy = () => {
    navigator.clipboard.writeText(state.code);
    setCopied(true); toast.success("Gift code copied"); setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen grain bg-cream" data-testid="gift-success-page">
      <header className="border-b border-line bg-cream/85">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 h-16 flex items-center">
          <Link to="/" className="flex items-center gap-2.5"><img src="/logo.png" alt="Write My Story" className="h-9 w-9 object-contain" /><Wordmark size="text-lg sm:text-xl" /></Link>
        </div>
      </header>

      <main className="max-w-xl mx-auto px-4 sm:px-6 py-16 relative z-10 text-center">
        {state.loading ? (
          <div className="py-10"><Loader2 className="animate-spin text-teal mx-auto" size={32} /><p className="text-subtle mt-4">Confirming your gift…</p></div>
        ) : state.paid ? (
          <div className="animate-fade-up">
            <span className="inline-flex w-16 h-16 rounded-full bg-sagelight text-teal items-center justify-center"><GiftIcon size={32} /></span>
            <h1 className="font-serif text-3xl text-charcoal mt-5">Your gift is ready!</h1>
            <p className="text-subtle mt-3 leading-relaxed">Thank you. We've emailed {state.recipient ? state.recipient : "your recipient"} their gift code and how to begin. You can share this code with them too:</p>
            <div className="mt-6 bg-white border border-line rounded-2xl p-6">
              <p data-testid="gift-code" className="font-serif text-3xl tracking-widest text-teal">{state.code}</p>
              <button data-testid="gift-copy-btn" onClick={copy} className="mt-4 inline-flex items-center gap-2 bg-teal hover:bg-tealdark text-cream px-5 py-2.5 rounded-full font-semibold text-sm">
                {copied ? <Check size={16} /> : <Copy size={16} />} {copied ? "Copied" : "Copy code"}
              </button>
              <button data-testid="gift-card-download-btn" onClick={downloadCard} disabled={downloading} className="mt-4 sm:ml-2 inline-flex items-center gap-2 bg-white border border-line hover:border-sage text-charcoal px-5 py-2.5 rounded-full font-semibold text-sm disabled:opacity-60">
                {downloading ? <Loader2 className="animate-spin" size={16} /> : <Download size={16} />} Printable gift card
              </button>
            </div>
            <p className="text-sm text-subtle mt-4">Print the gift card to hand over in person, or share the code directly. It gives a full 12 months of Write My Story.</p>
            <Link to="/" className="mt-8 inline-block text-teal font-semibold underline">Back to home</Link>
          </div>
        ) : (
          <div className="animate-fade-up">
            <h1 className="font-serif text-3xl text-charcoal">We couldn't confirm the gift just yet</h1>
            <p className="text-subtle mt-3">If you completed payment, your gift code will be emailed to your recipient shortly. Any question, email <a href="mailto:julietsullivan@outlook.com" className="text-teal underline">julietsullivan@outlook.com</a>.</p>
            <Link to="/gift" className="mt-8 inline-block text-teal font-semibold underline">Try again</Link>
          </div>
        )}
      </main>
    </div>
  );
}
